import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCheck,
  FiInfo,
  FiUploadCloud,
} from 'react-icons/fi';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api';
import { showToast } from '../utils/toast';

const allowedAccept =
  '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif';

type FieldErrors = {
  fullName?: string;
  phone?: string;
  email?: string;
  tradingExperience?: string;
  pnlFile?: string;
  communicationMethod?: string;
  paymentDetails?: string;
  webullApiKey?: string;
  webullApiSecret?: string;
  confirmAccepted?: string;
};

const BecomeMasterTrader: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [tradingPlatform, setTradingPlatform] = useState<'forex' | 'webull'>('webull');
  const [tradingExperience, setTradingExperience] = useState('');
  const [pnlFile, setPnlFile] = useState<File | null>(null);
  const [communicationMethod, setCommunicationMethod] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [webullApiKey, setWebullApiKey] = useState('');
  const [webullApiSecret, setWebullApiSecret] = useState('');
  const [confirmAccepted, setConfirmAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submitMutation = useMutation({
    mutationFn: async (payload: FormData) => authApi.applyMasterTrader(payload),
  });

  const platformLabel = useMemo(() => {
    return tradingPlatform === 'forex' ? 'Forex' : 'Webull';
  }, [tradingPlatform]);

  const handlePickFile = (file: File | null) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setFieldErrors((prev) => ({ ...prev, pnlFile: 'File size must be less than 20MB' }));
      return;
    }
    setFormError(null);
    clearFieldError('pnlFile');
    setPnlFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const newErrors: FieldErrors = {};

    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!phone.trim()) newErrors.phone = 'Phone is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    if (!tradingExperience.trim()) newErrors.tradingExperience = 'Trading experience is required';
    if (!pnlFile) newErrors.pnlFile = 'Please upload your last 3 months PnL file';
    if (!communicationMethod.trim()) newErrors.communicationMethod = 'Communication method is required';
    if (!paymentDetails.trim()) newErrors.paymentDetails = 'Payment details are required';
    if (!confirmAccepted) newErrors.confirmAccepted = 'Please confirm the Terms checkbox';

    if (tradingPlatform === 'webull') {
      if (!webullApiKey.trim()) newErrors.webullApiKey = 'Webull API Key is required';
      if (!webullApiSecret.trim()) newErrors.webullApiSecret = 'Webull API Secret is required';
    }

    if (Object.values(newErrors).some(Boolean)) {
      setFieldErrors(newErrors);
      return;
    }

    const formData = new FormData();
    formData.append('fullName', fullName.trim());
    formData.append('phone', phone.trim());
    formData.append('email', email.trim());
    formData.append('tradingPlatform', tradingPlatform);
    formData.append('tradingExperience', tradingExperience.trim());
    formData.append('communicationMethod', communicationMethod.trim());
    formData.append('paymentDetails', paymentDetails.trim());
    formData.append('confirmAccepted', confirmAccepted ? 'true' : 'false');
    if (tradingPlatform === 'webull') {
      formData.append('webullApiKey', webullApiKey.trim());
      formData.append('webullApiSecret', webullApiSecret.trim());
    }
    if (!pnlFile) return; // should be guarded by validation above
    formData.append('pnlFile', pnlFile);

    setIsSubmitting(true);
    try {
      const res = await submitMutation.mutateAsync(formData);
      if (res?.success) {
        showToast('success', 'Your application is applied. Our team will contact you soon.');
        window.location.assign('https://discord.gg/tEQ2Z83TUQ');
      } else {
        showToast('error', res?.message || 'Failed to submit application');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to submit application';
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e1016] py-8 sm:py-10 px-3 sm:px-6 lg:px-8">
      <div className="w-full max-w-xl min-w-0">
        <div className="text-center mb-6">
          <img src="/trade-pilot-logo.png" alt="TradePilot AI" className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">Become Master Trader</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Fill out this information to become a Master Trader of TradePilot AI</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-5">
            {formError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
                <FiInfo className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 break-anywhere">{formError}</span>
              </div>
            )}

            <SimpleField
              label="Full Name"
              required
              value={fullName}
              placeholder="Full Name"
              error={fieldErrors.fullName}
              onChange={(v) => {
                setFullName(v);
                clearFieldError('fullName');
              }}
            />
            <SimpleField
              label="Phone"
              required
              value={phone}
              placeholder="Phone"
              error={fieldErrors.phone}
              onChange={(v) => {
                setPhone(v);
                clearFieldError('phone');
              }}
            />
            <SimpleField
              label="Email"
              required
              type="email"
              value={email}
              placeholder="Email"
              error={fieldErrors.email}
              onChange={(v) => {
                setEmail(v);
                clearFieldError('email');
              }}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Trading Platform</label>
              <select
                value={tradingPlatform}
                onChange={(e) => {
                  const next = e.target.value as 'forex' | 'webull';
                  setTradingPlatform(next);
                  clearFieldError('webullApiKey');
                  clearFieldError('webullApiSecret');
                }}
                className="input-field bg-muted/20"
              >
                <option className='text-black' value="forex">Forex</option>
                <option className='text-black' value="webull">Webull</option>
              </select>
              <p className="text-xs text-gray-600 dark:text-gray-400">Selected: {platformLabel}</p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Please Upload Your Last 3 Months PNL <span className="text-red-500">*</span>
              </label>
              <div
                className="cursor-pointer rounded-xl border border-dashed border-primary-500/30 bg-muted/20 p-4 sm:p-6 text-center transition hover:border-primary-500/60"
                onClick={() => {
                  const el = document.getElementById('pnl-upload') as HTMLInputElement | null;
                  el?.click();
                }}
              >
                <input
                  id="pnl-upload"
                  type="file"
                  accept={allowedAccept}
                  className="hidden"
                  onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="rounded-full bg-primary/15 p-3">
                    <FiUploadCloud className="h-6 w-6 text-primary-500" />
                  </div>
                  <p className="text-sm text-foreground font-semibold">Click to upload</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    PDF, DOC/DOCX, XLS/CSV, JPG/JPEG, PNG, GIF
                  </p>
                  {pnlFile && <p className="text-xs text-primary-300 max-w-full break-anywhere">{pnlFile.name}</p>}
                </div>
              </div>
              {fieldErrors.pnlFile && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.pnlFile}</p>
              )}
            </div>

            <SimpleTextArea
              label="Trading Experience"
              required
              value={tradingExperience}
              placeholder="How much experience do you have in trading?"
              error={fieldErrors.tradingExperience}
              onChange={(v) => {
                setTradingExperience(v);
                clearFieldError('tradingExperience');
              }}
            />

            <SimpleField
              label="Preferred communication method"
              required
              value={communicationMethod}
              placeholder="Email, WhatsApp or Discord handle..."
              error={fieldErrors.communicationMethod}
              onChange={(v) => {
                setCommunicationMethod(v);
                clearFieldError('communicationMethod');
              }}
            />

            <SimpleTextArea
              label="Payment details for payouts"
              required
              value={paymentDetails}
              placeholder="Enter your paypal or other payment info"
              error={fieldErrors.paymentDetails}
              onChange={(v) => {
                setPaymentDetails(v);
                clearFieldError('paymentDetails');
              }}
            />

            {tradingPlatform === 'webull' ? (
              <div className="space-y-4">
                <div className="text-sm font-bold text-gray-900 dark:text-white">API Credentials (For Options)</div>
                <p className="text-[14px] text-gray-600 dark:text-gray-400 break-words">
                If you want to apply for Webull Master Trade, you first need to create and obtain your Webull API Key and API Secret.For detailed instructions, you can follow the Webull API documentation here: <a className='text-primary-500' href="https://developer.webull.co.jp/apis/docs/authentication/individual-application" target='_blank'>Click Here</a> Or, you can watch the step-by-step video tutorial here: <a className='text-primary-500' href="https://vimeo.com/1218308149" target='_blank'>Click Here</a></p>
                <SimpleField
                  label="API Key"
                  required
                  value={webullApiKey}
                  placeholder="Please enter your API Key"
                  error={fieldErrors.webullApiKey}
                  onChange={(v) => {
                    setWebullApiKey(v);
                    clearFieldError('webullApiKey');
                  }}
                />
                <SimpleField
                  label="API Secret"
                  required
                  value={webullApiSecret}
                  placeholder="Please enter your API Secret"
                  error={fieldErrors.webullApiSecret}
                  onChange={(v) => {
                    setWebullApiSecret(v);
                    clearFieldError('webullApiSecret');
                  }}
                />
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  We will use these credentials only for the Webull options verification step.
                </p>
              </div>
            ) : (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Forex selected: API credentials fields are not needed.
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmAccepted}
                onChange={(e) => {
                  setConfirmAccepted(e.target.checked);
                  clearFieldError('confirmAccepted');
                }}
                className="mt-1 h-8 w-8 shrink-0 accent-primary"
              />
              <span className="text-xs text-gray-700 dark:text-gray-200 leading-5">
                I confirm that I have read and agree to the Terms of Service and consent to receive communications from the Company using the contact information I provide.
              </span>
            </label>
            {fieldErrors.confirmAccepted && (
              <p className="text-xs text-red-500 -mt-2">{fieldErrors.confirmAccepted}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting || submitMutation.isPending}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || submitMutation.isPending ? (
                <>
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                  <span>Applying...</span>
                </>
              ) : (
                <>
                  <FiCheck className="h-5 w-5" />
                  <span>Apply</span>
                </>
              )}
            </button>
            <div className="text-center">Don't want to become a Master Trader? <Link to="/dashboard/overview" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">Go Back</Link></div>
          </form>
        </div>
      </div>
    </div>
  );
};

function SimpleField(props: {
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  type?: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {props.label}
        {props.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={props.type || 'text'}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className={`input-field bg-muted/20 ${
          props.error ? 'border border-red-500/60 focus:border-red-500/60' : ''
        }`}
      />
      {props.error && (
        <p className="mt-1 text-xs text-red-500">{props.error}</p>
      )}
    </div>
  );
}

function SimpleTextArea(props: {
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {props.label}
        {props.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <textarea
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className={`input-field bg-muted/20 min-h-[44px] ${
          props.error ? 'border border-red-500/60 focus:border-red-500/60' : ''
        }`}
      />
      {props.error && (
        <p className="mt-1 text-xs text-red-500">{props.error}</p>
      )}
    </div>
  );
}

export default BecomeMasterTrader;

